import { isApiError } from "@core/api/api-error";
import {
	HealthcareTeam,
	RegisteredPtInviteData,
	SharePatientOutput,
	UnregisteredPtInviteData,
} from "@domain/healthcare-team/healthcare-team";
import {
	HealthcareTeamErrorCodes,
	InvitationAlreadyAcceptedError,
	InvitationPtAlreadyInHctError,
	InvitationPtInDifferentCountryError,
	InvitationPtNotFoundError,
	InvitationPtNotInvitedError,
} from "@domain/healthcare-team/healthcare-team-errors";
import { HealthcareTeamService } from "@domain/healthcare-team/healthcare-team-service";

import { observable } from "micro-observables";

export type HealthcareTeamInvitationPatientData = {
	fullname: string;
	id: string;
};

export class HealthcareTeamManager {
	private readonly _healthcareTeam = observable<HealthcareTeam | null>(null);

	healthcareTeam = this._healthcareTeam.readOnly();

	constructor(private readonly healthcareTeamService: HealthcareTeamService) {}

	async fetchHealthcareTeam(patientId: string): Promise<void> {
		if (this._healthcareTeam.get()?.patientId !== patientId) {
			this._healthcareTeam.set(null);
		}
		const healthcareTeamMembers = await this.healthcareTeamService.fetchHealthcareTeam(patientId);
		this._healthcareTeam.set({ patientId, healthcareTeamMembers });
	}

	async sendInvitation(patientId: string, email: string, message?: string): Promise<void> {
		await this.healthcareTeamService.sendInvitation(patientId, email, message);
		await this.fetchHealthcareTeam(patientId);
	}

	async resendInvitation(patientId: string, hctMemberId: string): Promise<void> {
		await this.healthcareTeamService.resendInvitation(patientId, hctMemberId);
	}

	async sendUnregisteredInvitation(patientId: string, email: string): Promise<UnregisteredPtInviteData> {
		const result = await this.healthcareTeamService.sendUnregisteredInvitation(patientId, email);
		await this.fetchHealthcareTeam(patientId);
		return result;
	}

	async getInvitationDetails(patientId: string): Promise<SharePatientOutput> {
		try {
			return await this.healthcareTeamService.getInvitationDetails(patientId);
		} catch (e) {
			if (isApiError(e) && e.code === HealthcareTeamErrorCodes.PtInvitationAlreadyAccepted) {
				throw new InvitationAlreadyAcceptedError();
			}
			if (isApiError(e) && e.code === HealthcareTeamErrorCodes.PtNotInvited) {
				throw new InvitationPtNotInvitedError();
			}
			throw e;
		}
	}

	async deleteMember(patientId: string, hctMemberId: string): Promise<void> {
		await this.healthcareTeamService.deleteMember(patientId, hctMemberId);
		await this.fetchHealthcareTeam(patientId);
	}

	async leaveHealthcareTeam(patientId: string): Promise<void> {
		await this.healthcareTeamService.leaveHealthcareTeam(patientId);
	}

	async joinHealthcareTeam(code: string): Promise<HealthcareTeamInvitationPatientData> {
		return await this.healthcareTeamService.joinHealthcareTeam(code);
	}

	async confirmInvitation(patientId: string): Promise<void> {
		await this.healthcareTeamService.confirmInvitation(patientId);
	}

	async checkEmailRegistered(patientId: string, email: string): Promise<RegisteredPtInviteData> {
		try {
			return await this.healthcareTeamService.checkEmailRegistered(patientId, email);
		} catch (e) {
			if (isApiError(e) && e.code === HealthcareTeamErrorCodes.PtNotFoundByEmail) {
				throw new InvitationPtNotFoundError();
			}
			if (isApiError(e) && e.code === HealthcareTeamErrorCodes.PtInDifferentCountry) {
				throw new InvitationPtInDifferentCountryError();
			}
			if (isApiError(e) && e.code === HealthcareTeamErrorCodes.PtAlreadyInHealthcareTeam) {
				throw new InvitationPtAlreadyInHctError();
			}
			throw e;
		}
	}
}
